'use client'

import { cn } from '@/lib/utils'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { useState } from 'react'

type PaginationProps = React.ComponentProps<'nav'> & {
  totalPages: number
  page?: number
  onPageChange?: (page: number) => void
  highEmphasis?: boolean
  wrapperClassName?: string
}

export const Pagination = ({
  totalPages,
  page,
  onPageChange,
  className,
  highEmphasis,
  wrapperClassName,
  ...props
}: PaginationProps) => {
  const [internalPage, setInternalPage] = useState(1)
  const currentPage = page ?? internalPage

  const handlePageChange = (newPage: number) => {
    if (onPageChange) {
      onPageChange(newPage) // Controlled mode
    } else {
      setInternalPage(newPage) // Uncontrolled mode
    }
  }

  const getPageNumbers = () => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }

    if (currentPage <= 3) {
      return [1, 2, 3, '...', totalPages]
    }

    if (currentPage >= totalPages - 2) {
      return [1, '...', totalPages - 2, totalPages - 1, totalPages]
    }

    return [
      1,
      '...',
      currentPage - 1,
      currentPage,
      currentPage + 1,
      '...',
      totalPages,
    ]
  }

  return (
    <nav
      className={cn('flex justify-center', wrapperClassName)}
      aria-label='pagination'
      {...props}
    >
      <ul className={cn('flex items-center gap-2', className)}>
        <li>
          <button
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-full border border-neutral-200 text-neutral-900 transition enabled:hover:bg-neutral-200',
              highEmphasis &&
                'enabled:hover:bg-neutral-900 enabled:hover:text-background',
              currentPage === 1 && 'cursor-not-allowed opacity-50'
            )}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label='Trang trước'
            aria-disabled={currentPage === 1}
          >
            <ArrowLeft
              className={cn(
                'h-5 w-5',
                currentPage === 1 && 'cursor-not-allowed text-neutral-200'
              )}
            />
          </button>
        </li>

        {getPageNumbers().map((p, index) => (
          <li key={index}>
            {typeof p === 'number' ? (
              <button
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-full text-sm font-medium leading-5 text-neutral-900 transition hover:bg-neutral-200',
                  highEmphasis && 'hover:bg-neutral-900 hover:text-background',
                  currentPage === p
                    ? highEmphasis
                      ? 'bg-neutral-900 text-background'
                      : 'bg-neutral-200'
                    : ''
                )}
                onClick={() => handlePageChange(p)}
                aria-label={`Trang ${p}`}
              >
                {p}
              </button>
            ) : (
              <span className='cursor-default text-sm leading-4 text-neutral-900'>
                ...
              </span>
            )}
          </li>
        ))}

        <li>
          <button
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-full border border-neutral-200 text-neutral-900 transition enabled:hover:bg-neutral-200',
              highEmphasis &&
                'enabled:hover:bg-neutral-900 enabled:hover:text-background',
              currentPage === totalPages && 'cursor-not-allowed opacity-50'
            )}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label='Trang tiếp theo'
            aria-disabled={currentPage === totalPages}
          >
            <ArrowRight
              className={cn(
                'h-5 w-5',
                currentPage === totalPages &&
                  'cursor-not-allowed text-neutral-200'
              )}
            />
          </button>
        </li>
      </ul>
    </nav>
  )
}
